import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import TransactionsTable from "../../../components/UI/TransactionsTable";
import Api from "../../../services/api";
import moment from "moment";
import {
  SimpleGrid,
  Box,
  Heading,
  VStack,
  Flex,
  Text,
  useTheme,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
} from "@chakra-ui/react";
import {
  AiOutlineCreditCard,
  AiOutlineBarChart,
} from "react-icons/ai";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
} from "recharts";

import StatusCard from "../../../components/UI/StatusCard";
import MetricCard from "../../../components/UI/MetricCard";
import CustomTooltip from "../../../components/UI/CustomTooltip";
import DashboardHeader from "../../../components/Layout/DashboardHeader";
import { Loading } from "../../../components/loading";
import BillingByTagsChart from "../../../components/charts/BillingByTagsChart";
import DespesasPorCategoriaChart from "../../../components/charts/DespesasPorCategoriaChart";

const iconMap = {
  AiOutlineCreditCard,
  AiOutlineBarChart,
};

const formatCurrency = (value) => {
  if (typeof value === "number") {
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  }
  return value;
};

const formatValue = (value, name) => {
  if (typeof value === "number") {
    return formatCurrency(value);
  }
  return value;
};

const FinancialPage = () => {
  console.log("Renderizando FinancialPage");
  const theme = useTheme();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFiltering, setIsFiltering] = useState(false);

  // Default date range: last month to today
  const defaultStartDate = moment().subtract(1, "month").format("YYYY-MM-DD");
  const defaultEndDate = moment().format("YYYY-MM-DD");

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);

  const controllerRef = useRef(null);

  // -- Memoized computations to prevent re-renders, must be above conditional returns
  const memoizedSummaryCards = useMemo(() => {
    const summaryCards = data?.cards || [];
    return summaryCards.map((card, index) => (
      <StatusCard
        key={card.title + index}
        title={card.title}
        value={card.format === "currency" ? formatCurrency(card.value) : card.value}
        icon={iconMap[card.icon]}
        colorScheme="green"
      />
    ));
  }, [data?.cards]);

  const memoizedTransactions = useMemo(() => {
    const transactionsData = data?.transactions || [];
    // create a shallow copy to prevent mutating the original data
    const sorted = [...transactionsData].sort((a, b) => b.valor_FI_CAIXA - a.valor_FI_CAIXA).slice(0, 5);
    return sorted.map((tx, index) => {
      const isEntrada = tx.id_FI_TIPO_MOVIMENTACAO === 1;
      return (
        <Tr key={index} _hover={{ bg: "gray.800" }}>
          <Td>
              <Tag size="sm" colorScheme={isEntrada ? "green" : "red"} variant="subtle">
                {isEntrada ? "Entrada" : "Saída"}
              </Tag>
          </Td>
          <Td>
            <VStack align="flex-start" spacing={0}>
              <Text color="white" fontWeight="medium" fontSize="sm" isTruncated maxW="300px">
                {tx.clienteNome || "N/D"}
              </Text>
              <Text color="gray.300" fontSize="xs">
                {tx.desc_FI_CATEGORIA_MOVIMENTACAO || tx.detalhes_FI_CAIXA || "Sem categoria"}
              </Text>
            </VStack>
          </Td>
          <Td isNumeric>
            <Text color={isEntrada ? "green.400" : "red.400"} fontWeight="medium" fontSize="sm">
              {formatCurrency(tx.valor_FI_CAIXA)}
            </Text>
          </Td>
        </Tr>
      );
    });
  }, [data?.transactions]);

  const fetchData = useCallback(async (filters, triggeredByFilter = false) => {
    if (triggeredByFilter) {
      setIsFiltering(true);
    } else if (!data) {
      setLoading(true);
    }
    
    if (controllerRef.current) controllerRef.current.abort();
    controllerRef.current = new AbortController();
    const { signal } = controllerRef.current;
    
    try {
      const res = await Api.get("/api/finances/dashboard", {
        params: {
          startDate: filters.startDate,
          endDate: filters.endDate,
        },
        signal,
      });
      
      if (!signal.aborted) {
        setData(res.data);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Erro ao carregar dados do painel financeiro:", error);
        setData(null);
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false);
        setIsFiltering(false);
      }
    }
  }, [data]);

  const handleFilter = useCallback(() => {
    if (!startDate || !endDate) {
      alert("Selecione datas válidas para o filtro.");
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      alert("A Data Inicial não pode ser maior que a Data Final.");
      return;
    }

    fetchData({ startDate, endDate }, true);
  }, [startDate, endDate, fetchData]);

  const handleClearFilter = useCallback(() => {
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    fetchData({ startDate: defaultStartDate, endDate: defaultEndDate }, true);
  }, [fetchData, defaultStartDate, defaultEndDate]);

  useEffect(() => {
    fetchData({ startDate: defaultStartDate, endDate: defaultEndDate });

    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  if (loading) return <Loading />;

  const currentPeriodText = data?.period?.startDate && data?.period?.endDate
    ? `Período: ${moment(data.period.startDate).format("DD/MM/YYYY")} a ${moment(data.period.endDate).format("DD/MM/YYYY")}`
    : "Período: Último mês";

  if (!data || data.error) {
    return (
      <VStack spacing={8} align="stretch" p={6}>
        <DashboardHeader
          title="Dashboard Financeiro"
          periodText={data?.error ? "Erro ao carregar" : "Período: Mês atual"}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          onFilterClick={handleFilter}
          onClearFilter={handleClearFilter}
          isDisabled={isFiltering}
        />
        <Flex justify="center" align="center" h="50vh">
          <Text color="gray.500">
            {data?.error?.message || data?.error?.details || data?.error || "Não foi possível carregar os dados."}
          </Text>
        </Flex>
      </VStack>
    );
  }

  const {
    charts = {},
    transactions = [],
    metrics = {},
  } = data;

  const flowData = charts.flowData || [];
  const categoryData = charts.categoryData || [];

  // Build metrics array for display
  const complementaryMetrics = [
    { label: "Ticket Médio", value: formatCurrency(metrics.averageTicket || 0) },
    { label: "Clientes Únicos", value: String(metrics.payingClients || 0) },
    { label: "Média Produtos/MVNO", value: (metrics.avgProductsPerMvno || 0).toFixed(1) },
  ];


  const BLOCK_PADDING_X = 6;

  return (
    <VStack spacing={6} align="stretch" w="100%" p={0} pt={6}>
      {/* Header with filters */}
      <Box w="100%" px={BLOCK_PADDING_X}>
        <DashboardHeader
          title="Dashboard Financeiro"
          periodText={currentPeriodText}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          onFilterClick={handleFilter}
          onClearFilter={handleClearFilter}
          isDisabled={isFiltering}
        />
      </Box>

      {/* Summary Cards */}
      <Box w="100%" px={BLOCK_PADDING_X}>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          {memoizedSummaryCards}
        </SimpleGrid>
      </Box>

      {/* Charts Section */}
      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6} minH="400px" w="100%" px={BLOCK_PADDING_X}>
        {/* Pie Chart - Categories */}
        <Box
          gridColumn={{ base: "span 1", lg: "span 1" }}
          bg="surface"
          p={6}
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.800"
          minH="400px"
        >
          <Heading as="h3" size="sm" color="white" mb={4}>
            Despesas por Categoria
          </Heading>
          {categoryData.length > 0 ? (
            <Box>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    stroke="none"
                    labelLine={false}
                    minAngle={5}
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={<CustomTooltip formatValue={formatValue} />}
                    wrapperStyle={{ zIndex: 100 }}
                  />
                </PieChart>
              </ResponsiveContainer>
              {/* Custom scrollable legend - distributed grid */}
              <Box 
                mt={4}
                maxH="130px" 
                overflowY="auto" 
                pr={2}
                sx={{
                  '&::-webkit-scrollbar': { width: '4px' },
                  '&::-webkit-scrollbar-track': { bg: 'gray.800', borderRadius: 'full' },
                  '&::-webkit-scrollbar-thumb': { bg: 'gray.600', borderRadius: 'full' },
                }}
              >
                <SimpleGrid columns={2} spacingX={4} spacingY={2}>
                  {categoryData.map((entry, index) => (
                    <Flex key={index} align="center" gap={2} minW={0}>
                      <Box w="8px" h="8px" borderRadius="full" bg={entry.color} flexShrink={0} />
                      <Text fontSize="xs" color="gray.400" isTruncated title={entry.name}>
                        {entry.name}
                      </Text>
                    </Flex>
                  ))}
                </SimpleGrid>
              </Box>
            </Box>
          ) : (
            <Flex justify="center" align="center" h="300px">
              <Text color="gray.500">Sem dados de categorias</Text>
            </Flex>
          )}
        </Box>

        {/* Bar + Line Chart - Cash Flow */}
        <Box
          gridColumn={{ base: "span 1", lg: "span 2" }}
          bg="surface"
          p={6}
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.800"
        >
          <Heading as="h3" size="sm" color="white" mb={4}>
            Fluxo de Caixa Semanal
          </Heading>
          {flowData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart
                data={flowData}
                margin={{ top: 5, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255, 255, 255, 0.1)"
                />
                <XAxis
                  dataKey="name"
                  stroke={theme.colors.gray[700]}
                  tick={{ fill: theme.colors.gray[400], fontSize: 12 }}
                  interval="preserveStartEnd"
                  dy={10}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  stroke={theme.colors.gray[700]}
                  tick={{ fill: theme.colors.gray[400], fontSize: 12 }}
                  domain={[0, "auto"]}
                  tickFormatter={(value) => `R$${(value / 1000).toFixed(0)}k`}
                  axisLine={false}
                />
                <Tooltip
                  content={<CustomTooltip formatValue={formatValue} />}
                  wrapperStyle={{ zIndex: 100 }}
                />
                <Legend
                  verticalAlign="bottom"
                  align="center"
                  iconType="circle"
                  wrapperStyle={{
                    paddingTop: "20px",
                    fontSize: "12px",
                    color: theme.colors.gray[400],
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="entradas"
                  name="Entradas"
                  stroke="#48BB78"
                  strokeWidth={2}
                  dot={{ r: 4, fill: "#48BB78" }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="saidas"
                  name="Saídas"
                  stroke="#E53E3E"
                  strokeWidth={2}
                  dot={{ r: 4, fill: "#E53E3E" }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="saldo"
                  name="Saldo Acumulado"
                  stroke="#4299E1"
                  strokeWidth={2}
                  dot={{ r: 4, fill: "#4299E1" }}
                  activeDot={{ r: 6 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <Flex justify="center" align="center" h="300px">
              <Text color="gray.500">Sem dados de fluxo</Text>
            </Flex>
          )}
        </Box>
      </SimpleGrid>

      {/* Billing Chart - por Tags (substitui SETUP vs MRR) */}
      <Box w="100%" px={BLOCK_PADDING_X}>
        <BillingByTagsChart />
      </Box>

      {/* Despesas por Categoria Chart (100% Stacked) */}
      <Box w="100%" px={BLOCK_PADDING_X}>
        <DespesasPorCategoriaChart 
          parentStartDate={startDate} 
          parentEndDate={endDate} 
        />
      </Box>

      {/* Metrics and Transactions Summary */}
      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6} w="100%" px={BLOCK_PADDING_X}>
        <VStack spacing={4} align="stretch" gridColumn={{ base: "span 1", lg: "span 1" }}>
          <Heading as="h3" size="sm" color="secondary" fontWeight="semibold">
            Métricas Adicionais
          </Heading>
          <SimpleGrid columns={1} spacing={4}>
            {complementaryMetrics.map((metric) => (
              <MetricCard
                key={metric.label}
                value={metric.value}
                label={metric.label}
              />
            ))}
          </SimpleGrid>
        </VStack>

          <Box gridColumn={{ base: "span 1", lg: "span 2" }} bg="surface" p={6} borderRadius="lg" border="1px solid" borderColor="gray.800" overflow="hidden">
            <Heading as="h3" size="sm" color="white" mb={4}>
              Principais Transações
            </Heading>
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th color="gray.400">Tipo</Th>
                    <Th color="gray.400">Cliente / Descrição</Th>
                    <Th color="gray.400" isNumeric>Valor</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {memoizedTransactions}
                  {transactions.length === 0 && (
                    <Tr>
                      <Td colSpan={3} textAlign="center" color="gray.500" py={4}>
                        Sem transações no período
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          </Box>
      </SimpleGrid>

      {/* Transactions Table Section */}
      <Box
        bg="surface"
        p={6}
        borderRadius="lg"
        border="1px solid"
        borderColor="gray.800"
        mb={8}
        w="100%"
        mx={BLOCK_PADDING_X}
        maxW={`calc(100% - ${BLOCK_PADDING_X * 8}px)`}
      >
        <Heading as="h3" size="sm" color="white" mb={4}>
          Detalhe de Transações ({transactions.length})
        </Heading>
        
        <TransactionsTable data={transactions} />

      </Box>

      {/* Loading overlay when filtering */}
      {isFiltering && (
        <Flex
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="blackAlpha.600"
          justify="center"
          align="center"
          zIndex={1000}
        >
          <Spinner size="xl" color="green.400" thickness="4px" />
        </Flex>
      )}
    </VStack>
  );
};

export default FinancialPage;
